import React from 'react';
import AppleSignin from 'react-apple-signin-auth';
import { toast } from 'react-toastify';
import { socialLogin } from '../lib/api/user/userApi';
import { useUserAuth } from '../context/UserAuthContext';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../context/LangContext';

interface AppleSignInResponse {
  authorization: {
    code: string;
    id_token: string;
    state?: string;
  };
}

interface AppleLoginButtonProps {
  disabled?: boolean;
  onStartLoading?: () => void;
  onStopLoading?: () => void;
}

const AppleLoginButton: React.FC<AppleLoginButtonProps> = ({ 
  disabled = false, 
  onStartLoading,
  onStopLoading 
}) => {
  const { login } = useUserAuth();
  const navigate = useNavigate();
  const { lang } = useLang();

  const texts = {
    ko: {
      appleLoginSuccess: '애플 로그인 성공!',
      appleLoginError: '애플 로그인에 실패했습니다. 다시 시도해주세요.',
      serverLoginError: '서버 로그인에 실패했습니다. 다시 시도해주세요.',
    },
    en: {
      appleLoginSuccess: 'Apple login successful!',
      appleLoginError: 'Apple login failed. Please try again.',
      serverLoginError: 'Server login failed. Please try again.',
    },
  };

  const t = texts[lang];

  const handleAppleSignIn = async (response: AppleSignInResponse) => {
    try {
      console.log('=== Apple Sign-In 시작 ===');
      console.log('Apple Sign-In Response:', response);
      console.log('Authorization Code:', response.authorization.code);
      console.log('ID Token:', response.authorization.id_token);
      console.log('State:', response.authorization.state);
      
      onStartLoading?.();
      
      // Apple에서 받은 정보로 서버 API 호출
      console.log('=== 서버 소셜 로그인 API 호출 시작 ===');
      console.log('호출할 API:', 'socialLogin with provider: apple');
      console.log('Apple에서 받은 데이터를 서버로 전송합니다...');
      
      const socialLoginParams = {
        providerId: response.authorization.code, // Apple의 경우 code를 providerId로 사용
        accessToken: response.authorization.id_token, // id_token을 accessToken으로 사용
        email: '', // Apple은 첫 로그인 시에만 이메일 제공, 서버에서 id_token 파싱해서 추출
        name: '', // Apple은 첫 로그인 시에만 이름 제공, 서버에서 처리
      };
      console.log('서버로 전송할 데이터:', socialLoginParams);
      
      // socialLogin 함수는 '/api/user/apple/login' 경로로 POST 요청을 보냅니다
      console.log('socialLogin 함수 호출 중... (provider: apple)');
      
      // socialLogin 함수 존재 여부 확인
      if (typeof socialLogin !== 'function') {
        throw new Error('socialLogin 함수를 찾을 수 없습니다');
      }
      
      const socialLoginResponse = await socialLogin('apple', socialLoginParams);
      console.log('=== 애플 소셜 로그인 서버 응답 ===', socialLoginResponse);

      if (socialLoginResponse && socialLoginResponse.data && socialLoginResponse.data.statusCode === 200) {
        console.log('=== 서버 로그인 성공 - 사용자 데이터 구성 ===');
        
        // 서버에서 발급받은 토큰 사용
        const accessToken = socialLoginResponse.data.data?.token || response.authorization.id_token;

        console.log('=== 저장할 서버 응답 데이터 ===', socialLoginResponse.data.data);
        console.log('=== 저장할 토큰 ===', accessToken);
        console.log('🔍 needMoreInfo 값 확인:', socialLoginResponse.data.data?.needMoreInfo);

        // Context에 서버 응답 전체 데이터 전달
        console.log('=== Context에 로그인 정보 저장 시작 ===');
        login(socialLoginResponse.data.data, accessToken);
        console.log('=== Context에 로그인 정보 저장 완료 ===');
        
        toast.success(t.appleLoginSuccess);
        
        // needMoreInfo가 true인 경우 추가 정보 입력 페이지로, 아니면 홈으로 이동
        setTimeout(() => {
          if (socialLoginResponse.data.data?.needMoreInfo) {
            console.log('=== 추가 정보 입력 페이지로 이동 ===');
            navigate('/additional-info');
          } else {
            console.log('=== 홈으로 이동 시도 ===');
            navigate('/');
          }
        }, 100);
      } else {
        console.error('=== 서버 로그인 실패 ===', socialLoginResponse);
        toast.error(t.serverLoginError);
      }
      
    } catch (error: any) {
      console.error('=== Apple Sign-In Error 상세 정보 ===');
      console.error('Error object:', error);
      console.error('Error type:', typeof error);
      console.error('Error message:', error?.message);
      console.error('Error stack:', error?.stack);
      
      // 에러 메시지 파싱
      let errorMessage = t.appleLoginError;
      
      if (error?.data?.error?.customMessage) {
        errorMessage = error.data.error.customMessage;
      } else if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      onStopLoading?.();
    }
  };

  const handleAppleSignInError = (error: any) => {
    console.error('Apple Sign-In Error:', error);
    toast.error(t.appleLoginError);
    onStopLoading?.();
  };

  return (
    <AppleSignin
      authOptions={{
        clientId: 'com.thebidspace.web', // Services ID - 환경변수보다 고정값 우선
        scope: 'name email',
        redirectURI: process.env.REACT_APP_APPLE_REDIRECT_URI || 
          (window.location.hostname === 'localhost' 
            ? 'http://localhost:3000/'
            : `https://${window.location.hostname}/`), // 루트 경로로 수정
        state: 'bidspace_apple_login',
        nonce: `nonce_${Date.now()}`,
        usePopup: true, // 팝업 방식으로 다시 변경해서 테스트
      }}
      uiType="dark"
      className="apple-auth-btn"
      noDefaultStyle={true}
      buttonExtraChildren=""
      onSuccess={handleAppleSignIn}
      onError={handleAppleSignInError}
      skipScript={false}
      render={(props: any) => (
        <button
          {...props}
          disabled={disabled}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
            transition: 'transform 0.2s ease',
          }}
          onMouseEnter={(e) => {
            if (!disabled) {
              e.currentTarget.style.transform = 'scale(1.05)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <img 
            src="/login/apple.svg" 
            alt="애플" 
            style={{
              width: '50px',
              height: '50px',
              objectFit: 'contain'
            }}
          />
        </button>
      )}
    />
  );
};

export default AppleLoginButton;